// components/CollapsibleGridCard/CollapsibleGridCard.jsx

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, CardContent, Chip, Collapse, IconButton, Typography } from "@mui/material";
import { ErrorOutline, ExpandLess, ExpandMore } from "@mui/icons-material";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import ActionButtons from "./ActionButtons/ActionButtons";
import { useThemePreference } from "../contexts/ThemeContext";
import {
    buildGridInitialState,
    clearGridSessionState,
    getPersistableGridState,
    hasActiveFilters,
    readGridSessionState,
    writeGridSessionState,
} from "../lib/gridSessionState";

const CollapsibleGridCard = ({
    // Layout / header stuff
    title,
    description,
    linkLabel,
    linkHref = "#",
    className = "col-xl-12 col-md-12",
    variantClassName = "",
    headerClassName = "",
    titleClassName = "",
    descriptionClassName = "",
    toggleClassName = "",
    contentClassName = "",
    gridClassName = "",
    actionButtonsClassName = "",
    headerStyle,
    titleStyle,
    contentStyle,
    stickyHeader = false,
    stickyHeaderTop = 74,
    gridHeight,
    gridMaxHeight,

    // Optional filter content rendered next to the title
    filterNode, // ReactNode (e.g. your <FormControl> with Select)
    headerRightNode,

    // Optional action buttons
    actionButtons = [],

    // DataGrid basics
    rows,
    columns,
    getRowId,
    checkboxSelection = false,
    rowSelectionModel,
    onRowSelectionModelChange,

    // Optional custom footer component (e.g. TotalsFooter)
    footerComponent,

    // DataGrid config
    initialPageSize = 25,
    pageSizeOptions = [25, 50, 100],
    extraGridProps = {}, // for any other DataGridPro props you may want to pass
    apiRef,
    defaultSortModel,
    defaultVisibilityModel,
    sessionStorageKey,
    sessionUserId,
}) => {
    const { isDarkMode } = useThemePreference();
    const [collapsed, setCollapsed] = useState(false);
    const [gridStateSnapshot, setGridStateSnapshot] = useState({});
    const internalApiRef = useGridApiRef();
    const gridApiRef = apiRef || internalApiRef;
    const persistTimeoutRef = useRef(null);

    const {
        initialState: extraInitialState,
        onStateChange: extraOnStateChange,
        ...restGridProps
    } = extraGridProps;

    // Build slots (only override footer if we got one)
    const slots = {
        ...(extraGridProps.slots || {}),
        ...(footerComponent ? { footer: footerComponent } : {}),
    };

    const hasLink = Boolean(linkLabel);
    const persistedGridState = useMemo(
        () => readGridSessionState(sessionStorageKey, sessionUserId),
        [sessionStorageKey, sessionUserId]
    );

    useEffect(() => {
        setGridStateSnapshot(persistedGridState || {});
    }, [persistedGridState]);

    const defaultGridState = useMemo(() => ({
        pagination: {
            paginationModel: { pageSize: initialPageSize, page: 0 },
        },
        sorting: { sortModel: defaultSortModel },
        columns: {
            columnVisibilityModel: defaultVisibilityModel,
        },
        ...(extraInitialState || {}),
    }), [
        defaultSortModel,
        defaultVisibilityModel,
        extraInitialState,
        initialPageSize,
    ]);

    const initialState = useMemo(() => buildGridInitialState(defaultGridState, persistedGridState), [
        defaultGridState,
        persistedGridState,
    ]);

    const defaultStateSnapshot = useMemo(() => getPersistableGridState(defaultGridState), [defaultGridState]);

    const persistGridState = useCallback(() => {
        if (!sessionStorageKey || !gridApiRef.current?.exportState) return;

        const nextState = getPersistableGridState(gridApiRef.current.exportState());
        setGridStateSnapshot(nextState);
        writeGridSessionState(
            sessionStorageKey,
            sessionUserId,
            nextState
        );
    }, [gridApiRef, sessionStorageKey, sessionUserId]);

    const handleResetGridView = () => {
        window.clearTimeout(persistTimeoutRef.current);
        clearGridSessionState(sessionStorageKey, sessionUserId);
        setGridStateSnapshot(defaultStateSnapshot);

        if (typeof restGridProps.onFilterModelChange === "function") {
            restGridProps.onFilterModelChange(defaultStateSnapshot.filterModel || { items: [] });
        }

        if (typeof restGridProps.onSortModelChange === "function") {
            restGridProps.onSortModelChange(defaultStateSnapshot.sortModel || []);
        }

        if (typeof restGridProps.onColumnVisibilityModelChange === "function") {
            restGridProps.onColumnVisibilityModelChange(defaultStateSnapshot.columnVisibilityModel || {});
        }

        if (typeof restGridProps.onPaginationModelChange === "function") {
            restGridProps.onPaginationModelChange(defaultStateSnapshot.paginationModel || { page: 0, pageSize: initialPageSize });
        }

        gridApiRef.current?.setFilterModel?.(defaultStateSnapshot.filterModel || { items: [] });
        gridApiRef.current?.setSortModel?.(defaultStateSnapshot.sortModel || []);
        gridApiRef.current?.setColumnVisibilityModel?.(defaultStateSnapshot.columnVisibilityModel || {});
        gridApiRef.current?.setPaginationModel?.(defaultStateSnapshot.paginationModel || { page: 0, pageSize: initialPageSize });
        gridApiRef.current?.setDensity?.(defaultStateSnapshot.density || "standard");

        window.setTimeout(() => {
            persistGridState();
        }, 0);
    };

    const isSessionGrid = Boolean(sessionStorageKey);
    const filtersActive = isSessionGrid && hasActiveFilters(gridStateSnapshot?.filterModel);
    const hasSortChanges =
        isSessionGrid &&
        JSON.stringify(gridStateSnapshot?.sortModel || []) !== JSON.stringify(defaultStateSnapshot?.sortModel || []);
    const hasColumnChanges =
        isSessionGrid &&
        JSON.stringify(gridStateSnapshot?.columnVisibilityModel || {}) !== JSON.stringify(defaultStateSnapshot?.columnVisibilityModel || {});
    const showGridStatus = isSessionGrid && (filtersActive || hasSortChanges || hasColumnChanges);

    let gridStatusLabel = "";
    if (filtersActive && (hasSortChanges || hasColumnChanges)) {
        gridStatusLabel = "Saved filters and view active";
    } else if (filtersActive) {
        gridStatusLabel = "Saved filters active";
    } else if (hasSortChanges && hasColumnChanges) {
        gridStatusLabel = "Saved sort and columns active";
    } else if (hasSortChanges) {
        gridStatusLabel = "Saved sort active";
    } else if (hasColumnChanges) {
        gridStatusLabel = "Saved columns active";
    }

    const handleStateChange = (...args) => {
        if (sessionStorageKey) {
            window.clearTimeout(persistTimeoutRef.current);
            persistTimeoutRef.current = window.setTimeout(persistGridState, 150);
        }

        if (typeof extraOnStateChange === "function") {
            extraOnStateChange(...args);
        }
    };

    useEffect(() => () => {
        window.clearTimeout(persistTimeoutRef.current);
        persistGridState();
    }, [persistGridState]);

    const resolvedHeaderStyle = {
        ...(stickyHeader
            ? {
                position: "sticky",
                top: stickyHeaderTop,
                zIndex: 20,
                backgroundColor: isDarkMode ? undefined : "rgba(255, 255, 255, 0.98)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
            }
            : {}),
        ...headerStyle,
    };

    return (
        <div className={`${className} hive-collapsible-grid-card ${variantClassName}`.trim()} style={{ marginTop: "1em", marginBottom: "1em" }}>
            <div className="card">
                <div
                    className={`card-header d-flex align-items-center justify-content-between hive-collapsible-grid-header ${headerClassName}`.trim()}
                    style={resolvedHeaderStyle}
                >
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1, flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", width: "100%" }}>
                            <IconButton
                                onClick={() => setCollapsed(!collapsed)}
                                size="small"
                                sx={{ mr: 1 }}
                                className={`hive-collapsible-grid-toggle ${toggleClassName}`.trim()}
                            >
                                {collapsed ? <ExpandMore /> : <ExpandLess />}
                            </IconButton>

                            <h5 className={`hive-collapsible-grid-title ${titleClassName}`.trim()} style={titleStyle}>
                                {title}
                                {hasLink ? <> - <a className="hive-collapsible-grid-link" href={linkHref}>{linkLabel}</a></> : null}
                            </h5>

                            {/* Filter area (optional) */}
                            {filterNode && (
                                <div style={{ marginLeft: "4em" }}>
                                    {filterNode}
                                </div>
                            )}

                            {/* Action buttons (optional) */}
                            {actionButtons.length > 0 && (
                                <div className={actionButtonsClassName} style={{ marginLeft: "4em" }}>
                                    <ActionButtons buttons={actionButtons} />
                                </div>
                            )}
                        </Box>

                        {description && (
                            <Box
                                className={`hive-collapsible-grid-description ${descriptionClassName}`.trim()}
                                sx={{
                                    alignItems: "center",
                                    border: "1px solid",
                                    borderColor: "warning.main",
                                    borderRadius: "4px",
                                    color: "warning.dark",
                                    display: "flex",
                                    gap: 1.5,
                                    ml: 5,
                                    mt: 0.5,
                                    px: 2,
                                    py: 1.25,
                                    width: "fit-content",
                                    maxWidth: "calc(100% - 40px)",
                                }}
                            >
                                <ErrorOutline color="warning" fontSize="small" />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "inherit",
                                        fontWeight: 500,
                                        lineHeight: 1.5,
                                    }}
                                >
                                    {description}
                                </Typography>
                            </Box>
                        )}

                    </Box>

                    {headerRightNode && (
                        <Box sx={{ ml: 2 }}>
                            {headerRightNode}
                        </Box>
                    )}
                </div>

                <Collapse in={!collapsed} timeout="auto" unmountOnExit>
                    <CardContent className={`hive-collapsible-grid-content ${contentClassName}`.trim()} style={contentStyle}>
                        {showGridStatus && (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: 1,
                                    mb: 1.5,
                                    px: 0.5,
                                }}
                            >
                                <Chip
                                    size="small"
                                    color={filtersActive ? "warning" : "primary"}
                                    variant="outlined"
                                    label={gridStatusLabel}
                                />
                                <Button
                                    size="small"
                                    variant={isDarkMode ? "outlined" : "contained"}
                                    onClick={handleResetGridView}
                                    sx={{ minWidth: 0, textTransform: "none" }}
                                >
                                    Reset view
                                </Button>
                            </Box>
                        )}
                        <DataGridPro
                            className={`hive-collapsible-grid-data-grid ${gridClassName}`.trim()}
                            sx={{
                                ...(gridHeight ? { height: gridHeight } : {}),
                                ...(gridMaxHeight ? { maxHeight: gridMaxHeight } : {}),
                                backgroundColor: "var(--hive-grid-bg, white)",
                                color: "var(--hive-grid-text, inherit)",
                                borderColor: "var(--hive-grid-border, rgba(0, 0, 0, 0.12))",
                                "& .MuiDataGrid-columnHeaders": {
                                    background: "var(--hive-grid-header-bg, white)",
                                    color: "var(--hive-grid-header-text, inherit)",
                                    borderBottomColor: "var(--hive-grid-border, rgba(0, 0, 0, 0.12))",
                                },
                                "& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell": {
                                    borderColor: "var(--hive-grid-border, rgba(0, 0, 0, 0.12))",
                                },
                                "& .MuiDataGrid-footerContainer": {
                                    background: "var(--hive-grid-footer-bg, transparent)",
                                    borderTopColor: "var(--hive-grid-border, rgba(0, 0, 0, 0.12))",
                                },
                                "& .MuiCheckbox-root, & .MuiDataGrid-menuIcon button, & .MuiDataGrid-sortIcon": {
                                    color: "var(--hive-grid-icon, inherit)",
                                },
                                "& .MuiDataGrid-row:hover": {
                                    backgroundColor: "var(--hive-grid-row-hover, rgba(0, 0, 0, 0.04))",
                                },
                                "& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover": {
                                    backgroundColor: "var(--hive-grid-row-selected, rgba(25, 118, 210, 0.08))",
                                },
                                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                                    outline: "1px solid var(--hive-grid-focus, #1976d2)",
                                    outlineOffset: "-1px",
                                },
                                "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
                                    outline: "1px solid var(--hive-grid-focus, #1976d2)",
                                    outlineOffset: "-1px",
                                },
                            }}
                            rows={rows}
                            columns={columns}
                            getRowId={getRowId}
                            checkboxSelection={checkboxSelection}
                            rowSelectionModel={rowSelectionModel}
                            onRowSelectionModelChange={onRowSelectionModelChange}
                            pagination
                            initialState={initialState}
                            pageSizeOptions={pageSizeOptions}
                            slots={slots}
                            apiRef={gridApiRef}
                            onStateChange={handleStateChange}
                            {...restGridProps}
                        />
                    </CardContent>
                </Collapse>
            </div>
        </div>
    );
};

export default CollapsibleGridCard;
